import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Stack, Grid, Button, LinearProgress, Chip } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { KillSwitchModal } from '../../components/options/KillSwitchModal';
import { useOptionsStore } from '../../store/useOptionsStore';
import ShieldIcon from '@mui/icons-material/Shield';
import BoltIcon from '@mui/icons-material/Bolt';
import WarningIcon from '@mui/icons-material/Warning';

const RiskCockpitPage: React.FC = () => {
  const { todayPnl, marginUtilizationPct, killSwitchStatus, activePosition, setKillSwitchLevel, flattenPositions, fetchKillSwitch, fetchSpotData, fetchPositions, fetchHealthReport } = useOptionsStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTargetLevel, setSelectedTargetLevel] = useState('LEVEL_5_FORCE_SQUARE_OFF');

  useEffect(() => {
    fetchKillSwitch();
    fetchSpotData();
    fetchPositions();
    fetchHealthReport();
    const interval = setInterval(() => {
      fetchKillSwitch();
      fetchSpotData();
      fetchPositions();
      fetchHealthReport();
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const maxLossLimit = 15000;
  const currentLevel = killSwitchStatus?.currentLevel || null;

  const handleLevelClick = (level: string) => {
    if (level === 'LEVEL_5_FORCE_SQUARE_OFF' || level === 'LEVEL_6_BROKER_DISCONNECT') {
      setSelectedTargetLevel(level);
      setModalOpen(true);
    } else {
      setKillSwitchLevel(level, `Manual operator action to ${level}`);
    }
  };

  const handleConfirmEmergency = () => {
    if (selectedTargetLevel === 'LEVEL_5_FORCE_SQUARE_OFF') {
      flattenPositions();
    } else {
      setKillSwitchLevel(selectedTargetLevel, 'Emergency operator halt');
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Grid container spacing={2.5}>
        {/* Risk Cockpit Gauges */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <ShieldIcon sx={{ color: '#0284C7' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                NIFTY OPTIONS RISK COCKPIT
              </Typography>
            </Stack>

            <Stack spacing={2.5}>
              {/* Daily Loss Meter */}
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                    Daily P&L vs Loss Ceiling (₹{maxLossLimit.toLocaleString('en-IN')})
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: todayPnl >= 0 ? '#16A34A' : '#DC2626' }}>
                    {todayPnl >= 0 ? '+' : ''}₹{todayPnl.toLocaleString('en-IN')}
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={todayPnl >= 0 ? 0 : Math.min(100, (Math.abs(todayPnl) / maxLossLimit) * 100)} sx={{ height: 8, borderRadius: 4, bgcolor: '#F1F5F9' }} />
              </Box>

              {/* Margin Meter */}
              <Box>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#475569' }}>
                    Margin Utilization (Max Allowed: 70%)
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: marginUtilizationPct > 60 ? '#D97706' : '#16A34A' }}>
                    {marginUtilizationPct}%
                  </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={marginUtilizationPct} sx={{ height: 8, borderRadius: 4, bgcolor: '#F1F5F9' }} />
              </Box>

              {/* Greeks Grid */}
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 6 }}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Net Delta (Max ±0.30)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#0284C7' }}>
                      {activePosition?.greeks ? `${activePosition.greeks.delta >= 0 ? '+' : ''}${activePosition.greeks.delta}` : '+0.00'}{' '}
                      <Chip
                        size="small"
                        label={Math.abs(activePosition?.greeks?.delta ?? 0) < 0.22 ? 'NORMAL' : 'WATCH'}
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: Math.abs(activePosition?.greeks?.delta ?? 0) < 0.22 ? 'rgba(34, 197, 94, 0.12)' : 'rgba(245, 158, 11, 0.12)',
                          color: Math.abs(activePosition?.greeks?.delta ?? 0) < 0.22 ? '#16A34A' : '#D97706',
                          fontWeight: 700,
                        }}
                      />
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Net Gamma (Max -0.0030)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      {activePosition?.greeks ? activePosition.greeks.gamma : '-0.0000'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Theta Capture (Daily)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#16A34A' }}>
                      {activePosition?.greeks ? `${activePosition.greeks.theta >= 0 ? '+' : ''}₹${activePosition.greeks.theta}/d` : '+₹0/d'}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ color: '#64748B' }}>
                      Net Vega (Max -₹10k)
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: '#DC2626' }}>
                      {activePosition?.greeks ? `-₹${Math.abs(activePosition.greeks.vega)}` : '₹0'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Paper>
        </Grid>

        {/* 6-Level Kill-Switch Control Panel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
              <BoltIcon sx={{ color: '#DC2626' }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                6-LEVEL LIVE KILL-SWITCH HIERARCHY
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2 }}>
              Current Status: <strong>{currentLevel ? currentLevel.replace(/_/g, ' ') : 'CHECKING…'}</strong>
            </Typography>

            <Stack spacing={1.2}>
              <Button
                variant={currentLevel === 'LEVEL_0_ALL_SYSTEMS_GO' ? 'contained' : 'outlined'}
                onClick={() => handleLevelClick('LEVEL_0_ALL_SYSTEMS_GO')}
                sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
              >
                Level 0 — All Systems Go (Nominal Trading)
              </Button>
              <Button
                variant={currentLevel === 'LEVEL_1_STRATEGY_DISABLED' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => handleLevelClick('LEVEL_1_STRATEGY_DISABLED')}
                sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
              >
                Level 1 — Disable Strategy
              </Button>
              <Button
                variant={currentLevel === 'LEVEL_2_ENTRIES_DISABLED' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => handleLevelClick('LEVEL_2_ENTRIES_DISABLED')}
                sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
              >
                Level 2 — Disable New Entries (Maintain Open Positions)
              </Button>
              <Button
                variant={currentLevel === 'LEVEL_3_ADJUSTMENTS_DISABLED' ? 'contained' : 'outlined'}
                color="warning"
                onClick={() => handleLevelClick('LEVEL_3_ADJUSTMENTS_DISABLED')}
                sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
              >
                Level 3 — Disable Adjustments (Freeze Wings, Honor SL/TP)
              </Button>
              <Button
                variant={currentLevel === 'LEVEL_4_RISK_REDUCTION_ONLY' ? 'contained' : 'outlined'}
                color="error"
                onClick={() => handleLevelClick('LEVEL_4_RISK_REDUCTION_ONLY')}
                sx={{ justifyContent: 'flex-start', fontWeight: 600 }}
              >
                Level 4 — Risk Reduction Only (Close & Protective Buys Only)
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleLevelClick('LEVEL_5_FORCE_SQUARE_OFF')}
                sx={{ justifyContent: 'flex-start', fontWeight: 700 }}
              >
                Level 5 — Force Square-Off (Emergency Flatten All Positions)
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => handleLevelClick('LEVEL_6_BROKER_DISCONNECT')}
                sx={{ justifyContent: 'flex-start', fontWeight: 700 }}
              >
                Level 6 — Broker Disconnect & Engine Halt
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Modal */}
      <KillSwitchModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmEmergency}
        targetLevel={selectedTargetLevel}
      />
    </Box>
  );
};

export default RiskCockpitPage;
